import React, { useState } from "react";
import { Box, Button, Chip, Collapse, Paper, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { format } from "date-fns";
import {
  ChannelPricing,
  PricePeriod,
  formatUnitPrice,
} from "../../services/notificationCenterService";
import { CHANNEL_NAMES } from "./channelText";

const day = (iso: string) => format(new Date(iso), "d MMM yyyy");

/** "1 Nov 2026 – 30 Nov 2026", "From 1 Dec 2026"; times when it started and ended the same day. */
export const periodRange = (period: PricePeriod) => {
  if (!period.to) return `From ${day(period.from)}`;
  if (day(period.from) === day(period.to)) {
    return `${day(period.from)}, ${format(new Date(period.from), "HH:mm")} – ${format(new Date(period.to), "HH:mm")}`;
  }
  return `${day(period.from)} – ${day(period.to)}`;
};

const HistoryRow: React.FC<{ period: PricePeriod; now: Date; currency: string }> = ({
  period,
  now,
  currency,
}) => {
  const started = new Date(period.from) <= now;
  const ended = !!period.to && new Date(period.to) <= now;
  const state = !started ? "Upcoming" : ended ? "Past" : "Now";
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: 1,
        borderTop: "1px solid",
        borderColor: "divider",
        flexWrap: "wrap",
      }}
    >
      <Typography sx={{ fontSize: "0.86rem", flex: 1, minWidth: 180, color: ended ? "text.secondary" : "text.primary" }}>
        {periodRange(period)}
      </Typography>
      <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, fontVariantNumeric: "tabular-nums", minWidth: 70, textAlign: "right" }}>
        {period.price === null ? "Not set" : formatUnitPrice(period.price, currency)}
      </Typography>
      <Chip
        size="small"
        variant="outlined"
        label={state}
        color={state === "Now" ? "success" : state === "Upcoming" ? "warning" : "default"}
        sx={{ minWidth: 76 }}
      />
    </Box>
  );
};

const PriceCard: React.FC<{ pricing: ChannelPricing; currency: string }> = ({ pricing, currency }) => {
  const [open, setOpen] = useState(false);
  const now = new Date();
  const { current, upcoming, history } = pricing;
  const name = CHANNEL_NAMES[pricing.channel];

  return (
    <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, p: 2.25 }}>
      <Typography sx={{ fontWeight: 700 }}>{name}</Typography>

      {current && current.price !== null ? (
        <>
          <Typography sx={{ fontSize: "1.6rem", fontWeight: 700, mt: 0.5, fontVariantNumeric: "tabular-nums" }}>
            {formatUnitPrice(current.price, currency)}
            <Typography component="span" sx={{ fontSize: "0.85rem", fontWeight: 500, color: "text.secondary", ml: 0.75 }}>
              per delivered message
            </Typography>
          </Typography>
          <Typography sx={{ fontSize: "0.82rem", color: "text.secondary" }}>Since {day(current.from)}</Typography>
        </>
      ) : (
        <Typography sx={{ fontSize: "0.9rem", color: "text.secondary", mt: 0.75 }}>
          No price yet. We'll share it before {name} is charged.
        </Typography>
      )}

      {upcoming && (
        <Box sx={{ mt: 1.25, p: 1.25, borderRadius: 2, bgcolor: "#FFFBEB", border: "1px solid #FDE68A" }}>
          <Typography sx={{ fontSize: "0.85rem", color: "#92400E" }}>
            From <strong>{day(upcoming.from)}</strong>:{" "}
            <strong>{upcoming.price === null ? "no charge" : formatUnitPrice(upcoming.price, currency)}</strong> per message.
          </Typography>
        </Box>
      )}

      {history.length > 0 && (
        <>
          <Button
            size="small"
            onClick={() => setOpen((v) => !v)}
            endIcon={<ExpandMoreIcon sx={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 200ms" }} />}
            sx={{ textTransform: "none", mt: 1, px: 0.5 }}
          >
            {open ? "Hide price history" : `Price history (${history.length})`}
          </Button>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ mt: 0.5 }}>
              {history.map((period) => (
                <HistoryRow key={period.from} period={period} now={now} currency={currency} />
              ))}
            </Box>
          </Collapse>
        </>
      )}
    </Paper>
  );
};

/** What SMS and WhatsApp cost this organization, now, next and before. */
const PriceList: React.FC<{ pricing: { currency: string; channels: ChannelPricing[] } }> = ({ pricing }) => (
  <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" } }}>
    {pricing.channels.map((channel) => (
      <PriceCard key={channel.channel} pricing={channel} currency={pricing.currency} />
    ))}
  </Box>
);

export default PriceList;
